// Stage 3 of the Smart Asset Import pipeline: deterministic column -> field mapping.
//
// Replaces the legacy manual dropdown mapping with an exact alias lookup. A source header
// either matches a known alias (built-in or learned) or it does not. Matched headers are
// auto-mapped at confidence 100; unmatched headers are surfaced for the user to map once.
// Nothing is fuzzy-matched.

import { normalizeKey } from './textNormalize';
import { ALIASES, FIELD_DEFS, buildAliasIndex } from './headerAliases';

/**
 * Map detected headers to canonical fields.
 *
 * @param {Array<{index:number, raw:string}>} headers  kept headers from sheetProfiler
 * @param {Object<string,string>} learnedAliases        normalizedHeader -> fieldKey
 * @returns {{
 *   mapping: Object<string,{ sourceIndex:number, sourceHeader:string, via:'alias'|'learned', confidence:number }>,
 *   unmappedSourceColumns: Array<{ index:number, header:string }>,
 *   conflicts: Array<{ field:string, kept:number, dropped:number[] }>,
 *   unmappedRequiredFields: string[]
 * }}
 */
export const mapColumns = (headers, learnedAliases = {}) => {
  const index = buildAliasIndex(learnedAliases);
  const learnedKeys = new Set(Object.keys(learnedAliases || {}));

  // Group all candidate columns per field, then pick the best (lowest rank) for each so a
  // precise header ("Make") beats a looser alias ("Company Name") regardless of column order.
  const byField = {};
  const unmappedSourceColumns = [];

  (headers || []).forEach(({ index: colIndex, raw }) => {
    const key = normalizeKey(raw);
    const hit = key ? index[key] : undefined;
    if (!hit) {
      unmappedSourceColumns.push({ index: colIndex, header: raw });
      return;
    }
    const via = learnedKeys.has(key) ? 'learned' : 'alias';
    (byField[hit.field] = byField[hit.field] || []).push({ colIndex, raw, rank: hit.rank, via });
  });

  const mapping = {};
  const conflicts = [];
  Object.keys(byField).forEach((field) => {
    const cands = byField[field].slice().sort((a, b) => a.rank - b.rank); // best (lowest rank) first
    const win = cands[0];
    mapping[field] = { sourceIndex: win.colIndex, sourceHeader: win.raw, via: win.via, confidence: 100 };
    // Losing columns for this field are surfaced as unmapped so the user can place them elsewhere.
    cands.slice(1).forEach((c) => unmappedSourceColumns.push({ index: c.colIndex, header: c.raw }));
    if (cands.length > 1) conflicts.push({ field, kept: win.colIndex, dropped: cands.slice(1).map((c) => c.colIndex) });
  });

  // Which REQUIRED, non-auto-generated template fields have no source column?
  const unmappedRequiredFields = Object.keys(FIELD_DEFS).filter((f) => {
    const def = FIELD_DEFS[f];
    return def.required && !def.autoGenerated && !mapping[f];
  });

  return { mapping, unmappedSourceColumns, conflicts, unmappedRequiredFields };
};

/** Convenience: the list of canonical fields we know aliases for (for review dropdowns). */
export const KNOWN_FIELDS = Object.keys(ALIASES);
